/**
 * @fileoverview Matchmaking service for automatic player matching
 * Handles matchmaking queue, ELO-based matching, and game joining
 */

import {
  addMatchmakingEntry,
  queryMatchmakingQueue,
  removeMatchmakingEntry,
  MatchmakingEntry
} from '../firebase/matchmaking';
import { ZodiacEngine } from '../zodiacEngine/zodiacEngine';
import { Mode, State } from '../zodiacEngine/types';
import MMRService from '../MMR/MMRService';
import { UserDataService } from '../firebase/userDataService';
import { findCompatibleBot, isBotAccount } from '../bots/BotService';

// MatchmakingMode includes 'placement' for UI display purposes
// Placement is treated as 'competitive' for actual matchmaking
export type MatchmakingMode = 'competitive' | 'casual' | 'placement';

export interface MatchmakingConfig {
  mode: MatchmakingMode;
  userId: string;
  userRating: number;
  engine: ZodiacEngine;
  timeControlPreference?: 'sprint' | 'standard' | 'marathon' | 'any';  // Time control preference
  isPlaced?: boolean;  // Whether player has completed placement (affects cross-mode matching)
}

export interface MatchmakingStatus {
  searching: boolean;
  playersInQueue: number;
  estimatedWaitTime?: number;
  matchFound?: boolean;
  gameId?: string;
  weJoined?: boolean; // True if we joined opponent's game (need to navigate)
}

/**
 * Gets the ELO range based on mode and wait time
 */
function getELORange(mode: MatchmakingMode, waitTimeMs: number): number {
  if (mode === 'casual') {
    return Infinity; // No ELO restriction for casual
  }

  // Competitive mode - 200 ELO range (fixed)
  return 200;
}

/**
 * Finds the best opponent from available candidates
 * Prioritizes: 1) Closest ELO, 2) Longest wait time
 */
function findBestOpponent(
  opponents: MatchmakingEntry[],
  userRating: number
): MatchmakingEntry | null {
  if (opponents.length === 0) return null;

  // Sort by ELO difference (ascending), then by wait time (descending)
  const sorted = [...opponents].sort((a, b) => {
    const eloDiffA = Math.abs(a.rating - userRating);
    const eloDiffB = Math.abs(b.rating - userRating);

    if (eloDiffA !== eloDiffB) {
      return eloDiffA - eloDiffB; // Closer ELO first
    }

    // If ELO is same, prioritize longer wait time
    return a.searchStartTime - b.searchStartTime;
  });

  return sorted[0];
}

const BOT_FALLBACK_DELAY_MS = 20000; // 20 seconds before matching with a bot

export class MatchmakingService {
  private searching: boolean = false;
  private config: MatchmakingConfig | null = null;
  private searchStartTime: number = 0;
  private pollIntervalId: NodeJS.Timeout | null = null;
  private statusCallback: ((status: MatchmakingStatus) => void) | null = null;
  private myGameId: string | null = null; // Track the game we created
  private botMatchAttempted: boolean = false; // Prevent multiple bot match attempts

  /**
   * Start searching for a match
   */
  async startSearch(
    config: MatchmakingConfig,
    onStatusUpdate: (status: MatchmakingStatus) => void
  ): Promise<void> {
    if (this.searching) {
      throw new Error('Already searching for a match');
    }

    this.searching = true;
    this.config = config;
    this.searchStartTime = Date.now();
    this.statusCallback = onStatusUpdate;

    // Create game and add to queue
    try {

      // switchToLiveMove now properly awaits Firebase write
      const gameId = await config.engine.switchToLiveMove(config.userId);
      this.myGameId = gameId; // Track our game

      // Set ELO rating and matchmaking mode for game creator
      const eventManager = (config.engine as any).eventManager;
      if (eventManager) {
        const gameState = config.engine.getGameState();

        // Set legacy matchmakingMode field (used as fallback if per-player modes aren't synced)
        // Convert 'placement' to 'competitive' for the legacy field
        gameState.matchmakingMode = config.mode === 'placement' ? 'competitive' : config.mode;

        // Determine which color we are and set appropriate ELO and mode
        if (gameState.lightPlayer === config.userId) {
          eventManager.setLightElo(config.userRating);
          gameState.lightPlayerMatchmakingMode = config.mode;
        } else if (gameState.darkPlayer === config.userId) {
          eventManager.setDarkElo(config.userRating);
          gameState.darkPlayerMatchmakingMode = config.mode;
        }

        // Update Firebase with ELO data
        await eventManager.sendUpdateToRemote(false);
      } else {
      }


      // Use provided time control preference, default to 'standard'
      const timeControl = config.timeControlPreference || 'standard';


      // Convert 'placement' to 'competitive' for queue entry (placement is a UI distinction only)
      const queueMode = config.mode === 'placement' ? 'competitive' : config.mode;

      // Determine isPlaced: placement mode means NOT placed, casual is always "placed" (no placement needed)
      const isPlaced = config.mode === 'placement' ? false : (config.isPlaced ?? true);

      await addMatchmakingEntry({
        userId: config.userId,
        gameId: gameId,
        rating: config.userRating,
        searchStartTime: this.searchStartTime,
        preferredMode: queueMode,
        timeControlPreference: timeControl,
        isPlaced: isPlaced
      });


      // Start polling for matches
      this.startPolling();

    } catch (error) {
      this.searching = false;
      this.config = null;
      this.myGameId = null;
      throw error;
    }
  }

  /**
   * Cancel the current search
   */
  async cancelSearch(): Promise<void> {
    if (!this.searching || !this.config) return;

    // Stop polling
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }

    // Remove from queue
    try {
      await removeMatchmakingEntry(this.config.userId);
    } catch (error) {
    }

    this.searching = false;
    this.config = null;
    this.searchStartTime = 0;
    this.statusCallback = null;
    this.botMatchAttempted = false;
  }

  /**
   * Polls for available opponents and attempts to match
   */
  private startPolling(): void {
    // Initial search
    this.searchForOpponent();

    // Poll every 3 seconds
    this.pollIntervalId = setInterval(() => {
      this.searchForOpponent();
    }, 3000);
  }

  /**
   * Search for an opponent in the queue
   */
  private async searchForOpponent(): Promise<void> {
    if (!this.searching || !this.config) return;

    const waitTimeMs = Date.now() - this.searchStartTime;
    const eloRange = getELORange(this.config.mode, waitTimeMs);

    // Bot fallback: after 20 seconds with no human match, try matching with a bot
    if (waitTimeMs >= BOT_FALLBACK_DELAY_MS && !this.botMatchAttempted) {
      this.botMatchAttempted = true;
      const rawTimeControl = this.config.timeControlPreference || 'standard';
      // Bots only work with specific time controls, not 'any'
      if (rawTimeControl !== 'any') {
        try {
          const matched = await this.matchWithBot(rawTimeControl as 'sprint' | 'standard' | 'marathon');
          if (matched) return;
        } catch (error) {
        }
      }
    }

    try {

      // Query for compatible opponents
      // Convert 'placement' to 'competitive' for queue query (placement is a UI distinction only)
      const timeControl = this.config.timeControlPreference || 'standard';
      const queryMode = this.config.mode === 'placement' ? 'competitive' : this.config.mode;
      // Determine isPlaced for query: placement mode means NOT placed
      const myIsPlaced = this.config.mode === 'placement' ? false : (this.config.isPlaced ?? true);
      const opponents = await queryMatchmakingQueue(
        this.config.userId,
        this.config.userRating,
        eloRange,
        queryMode,
        timeControl,
        myIsPlaced
      );

      if (opponents.length > 0 && this.config) {
      }

      // Update status
      if (this.statusCallback) {
        this.statusCallback({
          searching: true,
          playersInQueue: opponents.length,
          estimatedWaitTime: this.estimateWaitTime(opponents.length, waitTimeMs)
        });
      }

      // Try to find a match
      const bestMatch = findBestOpponent(opponents, this.config.userRating);

      if (bestMatch) {

        // CRITICAL: Only join opponents who started searching BEFORE us
        // This prevents both players from trying to join each other simultaneously
        if (bestMatch.searchStartTime < this.searchStartTime) {
          await this.joinMatch(bestMatch);
        } else if (bestMatch.searchStartTime > this.searchStartTime) {
          // Don't join - let them join us since we've been waiting longer
        } else {
          // Exact same timestamp - use user ID to break tie
          if (this.config.userId < bestMatch.userId) {
            await this.joinMatch(bestMatch);
          } else {
          }
        }
      }

      // Timeout after 3 minutes - keep game but stop searching
      if (waitTimeMs > 180000) {
        this.stopSearching(false); // Don't remove from queue
      }

    } catch (error) {
      // Continue searching despite error
    }
  }

  /**
   * Join an opponent's game
   */
  private async joinMatch(opponent: MatchmakingEntry): Promise<void> {
    if (!this.config) return;


    try {
      // Stop polling immediately
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId);
        this.pollIntervalId = null;
      }

      // Remove both players from queue
      await Promise.all([
        removeMatchmakingEntry(this.config.userId),
        removeMatchmakingEntry(opponent.userId)
      ]);

      // Determine color assignment based on color balance
      // Using UserDataService to fetch profiles with correct nested schema

      const [myUserData, opponentUserData] = await Promise.all([
        UserDataService.getUserData(this.config.userId),
        UserDataService.getUserData(opponent.userId)
      ]);

      const myProfile = myUserData?.profile || null;
      const opponentProfile = opponentUserData?.profile || null;


      if (myProfile) {
      }

      if (opponentProfile) {
      }

      let assignMeAsDark = true; // Default: host is light, joiner (me) is dark

      if (myProfile && opponentProfile) {

        // Determine color assignment (opponent is player1, I am player2)
        const { player1IsLight } = MMRService.determineColorAssignment(opponentProfile, myProfile);

        if (!player1IsLight) {
          // Opponent should be dark, I should be light
          assignMeAsDark = false;
        } else {
        }
      } else {
      }

      // Join the opponent's game with retry logic (handle race condition)
      let joinAttempts = 0;
      let joinSuccessful = false;

      while (joinAttempts < 5 && !joinSuccessful) {
        try {
          await this.config.engine.joinGame(opponent.gameId, this.config.userId, assignMeAsDark);

          // Set ELO ratings for both players
          const eventManager = (this.config.engine as any).eventManager;
          if (eventManager) {
            const gameState = this.config.engine.getGameState();

            // Set my ELO
            if (gameState.lightPlayer === this.config.userId) {
              eventManager.setLightElo(this.config.userRating);
              eventManager.setDarkElo(opponent.rating);
            } else if (gameState.darkPlayer === this.config.userId) {
              eventManager.setDarkElo(this.config.userRating);
              eventManager.setLightElo(opponent.rating);
            }

            // Set per-player matchmaking modes for cross-mode match handling
            // This allows MMR to correctly handle casual vs placement matches
            const myMode = this.config.mode; // 'competitive', 'casual', or 'placement'
            const opponentMode = opponent.preferredMode === 'competitive' && !opponent.isPlaced
              ? 'placement' // Opponent is in placement (competitive queue but not placed)
              : opponent.preferredMode; // 'casual' or ranked 'competitive'

            if (gameState.lightPlayer === this.config.userId) {
              // I am light, opponent is dark
              gameState.lightPlayerMatchmakingMode = myMode;
              gameState.darkPlayerMatchmakingMode = opponentMode as any;
            } else {
              // I am dark, opponent is light
              gameState.darkPlayerMatchmakingMode = myMode;
              gameState.lightPlayerMatchmakingMode = opponentMode as any;
            }

            // Update Firebase with ELO data
            await eventManager.sendUpdateToRemote(false);
          } else {
          }

          joinSuccessful = true;
        } catch (error) {
          joinAttempts++;
          if (joinAttempts < 5) {
            // Wait 2 seconds before retrying (game might still be creating)
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            throw error; // Give up after 5 attempts
          }
        }
      }

      // Notify match found (we joined their game, so we need to navigate)
      if (this.statusCallback) {
        this.statusCallback({
          searching: false,
          playersInQueue: 0,
          matchFound: true,
          gameId: opponent.gameId,
          weJoined: true // We joined opponent's game
        });
      }

      this.searching = false;
      this.config = null;
      this.myGameId = null;

    } catch (error) {
      // If join fails completely, restart search
      this.searchForOpponent();
    }
  }

  /**
   * Match the searching player with a bot opponent.
   * Sets the bot as the opponent in the existing game and notifies via callback.
   * Returns true if bot match was successful.
   */
  private async matchWithBot(timeControl: 'sprint' | 'standard' | 'marathon'): Promise<boolean> {
    if (!this.config || !this.myGameId) return false;


    const botResult = await findCompatibleBot(timeControl, this.config.userRating);
    if (!botResult) {
      return false;
    }

    const { definition: bot, currentElo } = botResult;

    // Stop polling
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }

    // Remove player from matchmaking queue
    try {
      await removeMatchmakingEntry(this.config.userId);
    } catch (error) {
    }

    // Fetch player profile for color assignment
    const myUserData = await UserDataService.getUserData(this.config.userId);
    const myProfile = myUserData?.profile || null;

    // Bot always gets a random color (50/50), but respect player's color balance if available
    let assignBotAsDark = Math.random() < 0.5;
    if (myProfile) {
      // If player has played more as light, assign them dark (and bot as light)
      const balance = myProfile.mmr?.colorBalance ?? 0;
      if (balance > 0) {
        assignBotAsDark = false; // Player dark, bot light
      } else if (balance < 0) {
        assignBotAsDark = true; // Player light, bot dark
      }
    }

    // Set bot as opponent in the game via engine
    // IMPORTANT: engine.getGameState() returns a shallow copy, so we must access the
    // real game state through the gameStateManager to make mutations that persist.
    const eventManager = (this.config.engine as any).eventManager;
    const gameStateManager = (this.config.engine as any).gameStateManager;
    const gameState = gameStateManager.getGameState();

    // Set bot player info
    if (assignBotAsDark) {
      gameState.darkPlayer = bot.uid;
      gameState.darkPlayerUsername = bot.username;
      if (eventManager) {
        eventManager.setDarkElo(currentElo);
        eventManager.setLightElo(this.config.userRating);
      }
    } else {
      gameState.lightPlayer = bot.uid;
      gameState.lightPlayerUsername = bot.username;
      // Player needs to become dark
      gameState.darkPlayer = this.config.userId;
      gameState.darkPlayerUsername = myProfile?.username || '';
      if (eventManager) {
        eventManager.setLightElo(currentElo);
        eventManager.setDarkElo(this.config.userRating);
      }
    }

    // Set bot game flags
    gameState.isBotGame = true;
    gameState.botPlayerId = bot.uid;
    gameState.botAiModel = bot.config.aiModel;
    gameState.botTimeFrame = bot.config.timeFrame;

    // Set matchmaking modes
    const myMode = this.config.mode;
    if (assignBotAsDark) {
      gameState.lightPlayerMatchmakingMode = myMode;
      gameState.darkPlayerMatchmakingMode = myMode === 'placement' ? 'competitive' : myMode;
    } else {
      gameState.darkPlayerMatchmakingMode = myMode;
      gameState.lightPlayerMatchmakingMode = myMode === 'placement' ? 'competitive' : myMode;
    }
    gameState.matchmakingMode = myMode === 'placement' ? 'competitive' : myMode;

    // Transition game state from Searching to KingSelection
    // (mirrors what joinGame() does via gameStateManager.nextGameState())
    gameState.state = State.KingSelection;

    // Switch to local AI mode — bot games run entirely on the client.
    // sendUpdateToRemote() will no-op since mode is no longer Live.
    // This eliminates all per-move Firebase writes, piece ID regeneration bugs,
    // and Firebase listener race conditions.
    gameStateManager.setMode(Mode.AI);

    // Write bot assignment to the existing Firebase game document (one-time, for game records)
    if (eventManager?.gameId) {
      try {
        const { updateGamesDocument } = await import('../firebase/firebase');
        await updateGamesDocument(eventManager.gameId, {
          isBotGame: true,
          botPlayerId: bot.uid,
          botAiModel: bot.config.aiModel,
          botTimeFrame: bot.config.timeFrame,
          state: 'KingSelection',
          lightPlayer: gameState.lightPlayer,
          darkPlayer: gameState.darkPlayer,
          lightPlayerUsername: gameState.lightPlayerUsername || '',
          darkPlayerUsername: gameState.darkPlayerUsername || '',
        });
      } catch (error) {
      }
    }


    // Notify match found (we stay in our own game, no navigation needed)
    if (this.statusCallback) {
      this.statusCallback({
        searching: false,
        playersInQueue: 0,
        matchFound: true,
        gameId: this.myGameId!,
        weJoined: false, // We stay in our own game
      });
    }

    this.searching = false;
    this.config = null;
    this.myGameId = null;
    this.botMatchAttempted = false;
    return true;
  }

  /**
   * Stop searching (internal)
   */
  private async stopSearching(removeFromQueue: boolean = true): Promise<void> {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }

    if (removeFromQueue && this.config) {
      try {
        await removeMatchmakingEntry(this.config.userId);
      } catch (error) {
      }
    }

    this.searching = false;
    this.config = null;
  }

  /**
   * Estimate wait time based on queue size and current wait
   */
  private estimateWaitTime(queueSize: number, currentWaitMs: number): number {
    if (queueSize > 0) {
      // Someone in queue - match likely soon
      return Math.max(5, 30 - Math.floor(currentWaitMs / 1000));
    }

    // Empty queue - estimate based on mode
    if (this.config?.mode === 'competitive') return 60;
    return 30; // casual
  }

  /**
   * Check if currently searching
   */
  isSearching(): boolean {
    return this.searching;
  }
}

// Singleton instance
export const matchmakingService = new MatchmakingService();
